/**
 * Haptics: one vocabulary of intents (what happened), mapped to the Capacitor plugin in one place.
 *
 * Why a module and not ad-hoc plugin calls: the old wrapper imported the plugin on every call (the first
 * haptic landed late) and made three bridge calls per selection tick. Here the plugin is imported statically,
 * each intent is throttled (40ms), the selection generator stays warm between ticks (one bridge call per tick),
 * and the creator can turn haptics off (`lh:haptics`, default on). Everything is a no-op on the web.
 *
 * Rules (docs/harness-v2/MOBILE.md): no haptic on plain taps or navigation; for network actions fire on the
 * result (`success` / `error`), not the tap; at most one haptic per user action.
 */
import { Capacitor } from '@capacitor/core'
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics'
import { readonly, ref } from 'vue'
import { isNativePlatform } from '@/utils/platform'

export type HapticIntent =
    | 'selection' // a value or detent changed (tabs, segments, pickers, sheet detents, drag ticks)
    | 'toggle' // a switch, checkbox, star or chip flipped
    | 'commit' // a gesture crossed its commit point (swipe archive, long-press menu, drop)
    | 'back' // an interactive pop committed (edge swipe)
    | 'success' // a request succeeded (sent, saved, approved)
    | 'warning' // a destructive confirm opened, an undo toast appeared
    | 'error' // a request failed
    | 'heavy' // a confirmed destructive action (delete, bulk archive)
    | 'light'
    | 'medium'

export const HAPTICS_STORAGE_KEY = 'lh:haptics'
/** At most one haptic per intent in this window. */
export const HAPTIC_THROTTLE_MS = 40
/** A one-off selection tick keeps the generator prepared this long, so the next tick is one bridge call. */
export const SELECTION_IDLE_MS = 1000

const IMPACT: Partial<Record<HapticIntent, ImpactStyle>> = {
    toggle: ImpactStyle.Light,
    light: ImpactStyle.Light,
    back: ImpactStyle.Light,
    commit: ImpactStyle.Medium,
    medium: ImpactStyle.Medium,
    heavy: ImpactStyle.Heavy,
}
const NOTIFY: Partial<Record<HapticIntent, NotificationType>> = {
    success: NotificationType.Success,
    warning: NotificationType.Warning,
    error: NotificationType.Error,
}

function readSetting(): boolean {
    try {
        return localStorage.getItem(HAPTICS_STORAGE_KEY) !== '0'
    } catch {
        return true
    }
}

const enabled = ref(readSetting())
/** The creator's setting (Settings → Haptics). Read-only; change it with `setHapticsEnabled`. */
export const hapticsEnabled = readonly(enabled)

export function setHapticsEnabled(on: boolean): void {
    enabled.value = on
    try {
        localStorage.setItem(HAPTICS_STORAGE_KEY, on ? '1' : '0')
    } catch {
        // Storage unavailable (private mode): the setting lasts for this session.
    }
    if (!on) endSelection()
}

function canFire(): boolean {
    return enabled.value && isNativePlatform() && Capacitor.isPluginAvailable('Haptics')
}

/** Bridge calls are fire-and-forget: a failed haptic must never surface. */
function send(p: Promise<unknown> | undefined): void {
    p?.catch(() => {})
}

const lastAt = new Map<HapticIntent, number>()
function throttled(intent: HapticIntent): boolean {
    const now = Date.now()
    const last = lastAt.get(intent)
    if (last !== undefined && now - last < HAPTIC_THROTTLE_MS) return true
    lastAt.set(intent, now)
    return false
}

// ── selection generator ─────────────────────────────────────────────────────
// iOS only ticks between selectionStart and selectionEnd (the plugin creates the generator in start), so a
// tick needs a started generator. Keep it started while ticks keep coming, end it after a second of quiet.
let selectionStarted = false
let selectionIdle: ReturnType<typeof setTimeout> | null = null
let openSessions = 0

function clearIdle() {
    if (selectionIdle) clearTimeout(selectionIdle)
    selectionIdle = null
}
function ensureSelection() {
    clearIdle()
    if (selectionStarted) return
    selectionStarted = true
    send(Haptics.selectionStart())
}
function endSelection() {
    clearIdle()
    if (!selectionStarted) return
    selectionStarted = false
    send(Haptics.selectionEnd())
}
function scheduleSelectionEnd() {
    clearIdle()
    if (openSessions > 0) return
    selectionIdle = setTimeout(endSelection, SELECTION_IDLE_MS)
}

/**
 * Fire a haptic for what just happened. Returns whether a bridge call was made (false on the web, when
 * turned off, or when throttled).
 */
export function haptic(intent: HapticIntent): boolean {
    if (!canFire() || throttled(intent)) return false
    if (intent === 'selection') {
        ensureSelection()
        send(Haptics.selectionChanged())
        scheduleSelectionEnd()
        return true
    }
    const style = IMPACT[intent]
    if (style) {
        send(Haptics.impact({ style }))
        return true
    }
    const type = NOTIFY[intent]
    if (type) {
        send(Haptics.notification({ type }))
        return true
    }
    return false
}

export interface SelectionSession {
    /** One detent / item crossed. Throttled like `haptic('selection')`. */
    tick(): void
    /** The drag ended. Safe to call twice. */
    end(): void
}

/**
 * For drags that cross many items (a tab-bar lens, a picker wheel, a slider): starts the selection generator
 * once, ticks per crossing, ends it on `end()`.
 */
export function selectionSession(): SelectionSession {
    let open = true
    openSessions += 1
    if (canFire()) ensureSelection()
    return {
        tick() {
            if (!open || !canFire() || throttled('selection')) return
            ensureSelection()
            send(Haptics.selectionChanged())
        },
        end() {
            if (!open) return
            open = false
            openSessions = Math.max(0, openSessions - 1)
            if (openSessions === 0) endSelection()
        },
    }
}

/** Test-only: forget throttle timestamps and the generator state. */
export function __resetHapticsForTests(): void {
    lastAt.clear()
    clearIdle()
    selectionStarted = false
    openSessions = 0
    enabled.value = readSetting()
}
